// All user and reference data returned from API calls should be stored here
import HTTP from '@/services/baseHttpSvc.js';
import auth from '@/services/authSvc';

// import axios from 'axios';
import _ from 'lodash';

const state = {
  // the logged-in user
  user: {},
  // If available, the co-branded header image
  partnerHeaderImageSrc: null,
  // access codes that the user can view - for filtering
  accessCodes: [],
  // available wellness programs - for filtering
  wellnessPrograms: [],
  // available referrers - for filtering
  referrers: [],
  // Api config - for using configuration from API
  apiConfig: {},
  // Auth Object
  auth: auth
};

// store is accessed from components via getters only
const getters = {
  user: state => state.user,
  permissions: state => state.permissions,
  isClinicalPortalView: () => state.auth.isClinicalPortal(),
  isLoggedIn: () => auth.isLoggedIn() && auth.isVerified(),
  isDashboardUser: () => auth.isDashboardUser(),
  isCoach: () => auth.isCoach(),
  isCoachSupervisor: () => auth.isCoachSupervisor(),
  username: state => state.user ? state.user.firstName : '',
  provider: state => {
    if (!_.isEmpty(state.user)) {
      return {
        organization: state.user.organization.shortName,
        accessCode: state.user.accessCode.accessCode,
        group: state.user.accessCodeGroup !== null ? state.user.accessCodeGroup.shortName : null,
        preferences: state.user.providerPreferences
      };
    }
    return {};
  },
  partnerHeaderImageSrc: state => state.partnerHeaderImageSrc,
  wellnessPrograms: state => state.wellnessPrograms,
  wellnessProgramByName: (state) => (name) => {
    if (_.isEmpty(state.wellnessPrograms)) return '';
    let prog = _.filter(state.wellnessPrograms, { 'shortName': name });
    if (prog.length === 1) {
      return prog[0].label;
    }
    return '';
  },
  accessCodes: state => state.accessCodes,
  referrers: state => state.referrers,
  apiConfig: state => state.apiConfig
};

const actions = {
  authChanged(store) {
    store.commit('setAuth', {...auth});
  },
  loadUserData(store) {
    HTTP.get('/user').then(response => {
      store.commit('setUser', response.data);
      let provider = store.getters.provider;
      _getHeaderImage(provider).then(headerSrc => {
        store.commit('setPartnerHeader', headerSrc);
      });
      store.commit('setPermissions', auth.getPermissions());
    });
  },
  loadWellnessPrograms(store) {
    HTTP.get('/user/metadata/tags/components?name=CONDITION').then(function(response) {
      let focuses = _.get(response, 'data[0].tagList');
      if (focuses) {
        store.commit('setWellnessPrograms', focuses.filter(t => t.shortName !== 'condition:smi' && t.shortName !== 'condition:all'));
      }
    });
  },
  loadAccessCodes(store) {
    HTTP.get('/patients/lookup/accesscodes').then(function(response) {
      store.commit('setAccessCodes', response.data);
    });
  },
  loadReferrers(store) {
    HTTP.get('/patients/lookup/referrers').then(function(response) {
      store.commit('setReferrers', response.data);
    });
  },
  loadApiConfig(store) {
    HTTP.get('/config').then(response => {
      store.commit('setApiConfig', response.data);
    });
  },
  goToLogout(state) {
    auth.logout();
  },
  logoutUser(store) {
    return HTTP.post('/user/logout').finally(() =>
      store.commit('resetUserState')
    );
  }
};

const mutations = {
  setAuth(state, auth) {
    state.auth = auth;
  },
  setUser(state, user) {
    state.user = user;
  },
  setPartnerHeader(state, headerSrc) {
    state.partnerHeaderImageSrc = headerSrc;
  },
  setWellnessPrograms(state, programs) {
    state.wellnessPrograms = programs;
  },
  setAccessCodes(state, accessCodes) {
    state.accessCodes = accessCodes;
  },
  setReferrers(state, referrers) {
    state.referrers = referrers;
  },
  setApiConfig(state, config) {
    state.apiConfig = config;
  },
  setPermissions(state, permissions) {
    state.permissions = permissions;
  },
  resetUserState(state) {
    state.user = {};
    auth.clearToken();
  }
};

function _getHeaderImage(provider) {
  if (!provider) {
    return false;
  }

  var url = '/collateral/headers/organizations/' + provider.organization;
  if (provider.accessCode) {
    url += '/accesscodes/' + provider.accessCode;
  } else if (provider.group) {
    url += '/groups/' + provider.group;
  }

  // Fetch the header (either group or org level), and return only the image.
  return HTTP.get(url).then(resp => _getImageFromHeader(resp.data));
};

// Filters out just the image data from an org's header.
function _getImageFromHeader(headerData) {
  if (!headerData.content) {
    return false;
  }

  return _.get(headerData.content.filter(
    headerContent => headerContent.target === 'image')[0], 'replacement');
}

export default {
  state,
  getters,
  actions,
  mutations
};
